import { useCallback, useSyncExternalStore } from 'react';
import { DEFAULT_LANGUAGE, isLanguageCode, LanguageCode } from './languages';
import { en, TranslationKey, Translations } from './translations/en';
import { hi } from './translations/hi';
import { ta } from './translations/ta';
import { kn } from './translations/kn';
import { mr } from './translations/mr';
import { bn } from './translations/bn';

export { LANGUAGES } from './languages';
export type { LanguageCode } from './languages';
export type { TranslationKey } from './translations/en';

const DICTIONARIES: Record<LanguageCode, Translations> = { en, hi, ta, kn, mr, bn };
const STORAGE_KEY = 'language';

const listeners = new Set<() => void>();

/** The saved language, read fresh so every store (and test) agrees with localStorage. */
const readLanguage = (): LanguageCode => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return isLanguageCode(saved) ? saved : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
};

export const setLanguage = (language: LanguageCode) => {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Storage blocked (private mode): the choice lasts until the page reloads.
  }
  document.documentElement.lang = language;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

document.documentElement.lang = readLanguage();

type Vars = Record<string, string | number>;

/** Text for `key` in `language`, falling back to English, with `{name}` placeholders filled. */
export const translate = (language: LanguageCode, key: TranslationKey, vars?: Vars) => {
  const text = DICTIONARIES[language][key] || en[key];
  return vars ? text.replace(/\{(\w+)\}/g, (match, name) => String(vars[name] ?? match)) : text;
};

/**
 * Current UI language and a `t()` for it. Components re-render when the language changes.
 * `tSport` translates the sport labels from `src/config/sports.ts`, leaving unknown ones as is.
 */
export const useTranslation = () => {
  const language = useSyncExternalStore(subscribe, readLanguage);
  const t = useCallback(
    (key: TranslationKey, vars?: Vars) => translate(language, key, vars),
    [language]
  );
  const tSport = useCallback(
    (label: string) => {
      const key = `sport.${label}`;
      return key in en ? translate(language, key as TranslationKey) : label;
    },
    [language]
  );
  return { language, setLanguage, t, tSport };
};
